import { gqlClient } from 'services/api/gql/gqlClient';
import userUtils from 'utils/User';
import { getPartnerSlug, getLinkSuccessUrl, getLinkFailureUrl } from 'components/LoyaltyConnection/constants';

// Sent as a full query (queryBody) since this is not a persisted operation.
// The partner is identified by name (the URL slug, e.g. "starbucks").
const LINK_PARTNER_ACCOUNT = `mutation linkPartnerAccount($clientId: ID!, $partnerName: String!, $consent: Boolean!, $successUrl: String, $failureUrl: String) {
    linkPartnerAccount(clientId: $clientId, partnerName: $partnerName, consent: $consent, successUrl: $successUrl, failureUrl: $failureUrl) {
        partnerOAuthUrl
        linkRequestId
    }
}`;

/**
 * Initiates partner account linking and returns the partner OAuth URL. The caller
 * redirects the whole window to this URL (LC-145).
 *
 * Headers/URL/token are added automatically by the GQL client middlewares
 * (Authorization/Seph-Access-Token, X-Api-Key, Apollographql-Client-*, and the
 * gqlAPIEndpoint URL). `clientId` (the current profile/loyalty id) is a mandatory
 * argument and is also sent as the X-Client-Id header. After the OAuth flow the
 * provider redirects back to this page via successUrl/failureUrl (/starbucks on
 * success, /starbucks?isSuccess=false on failure), which the landing page detects.
 *
 * @returns {Promise<string>} partnerOAuthUrl
 */
async function initiateAccountLink() {
    const clientId = userUtils.getProfileId();

    // Fail fast: clientId is a required GraphQL arg (ID!) and the X-Client-Id header.
    // The landing page's auth gate guarantees a profile id before the link modal is
    // reachable, but guard here as defense-in-depth against ungated future callers.
    if (!clientId) {
        throw new Error('[LoyaltyConnection] Missing profileId/clientId for linkPartnerAccount');
    }

    // On success the provider returns to the partner page /starbucks (linked state
    // detected on load); on failure it returns to /starbucks?isSuccess=false, which
    // triggers the error popup.
    const successUrl = getLinkSuccessUrl();
    const failureUrl = getLinkFailureUrl();

    const data = await gqlClient.query({
        operationName: 'linkPartnerAccount',
        // `query` (a persisted-query hash) is required by gqlClient.query's
        // validation, but `queryBody` takes precedence in BodyMiddleware and
        // sends the full mutation — used here because this operation is not a
        // persisted query.
        query: 'linkPartnerAccount',
        queryBody: LINK_PARTNER_ACCOUNT,
        variables: {
            clientId,
            partnerName: getPartnerSlug(),
            consent: true,
            successUrl,
            failureUrl
        },
        headers: { 'X-Client-Id': clientId }
    });

    return data?.linkPartnerAccount?.partnerOAuthUrl || '';
}

export default initiateAccountLink;
